import { Component, OnInit, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil, finalize } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { RegulatoryDataService } from 'src/app/core/services/regulatory-data.service';

interface ProcessedDetail {
  subject: string;
  info: string;
  processedTextSegments: Array<{ text: string; isLink: boolean; url?: string }>;
}

interface ProcessedTopic {
  title: string;
  details: ProcessedDetail[];
}

@Component({
    selector: 'app-regulary-data',
    templateUrl: './regulary-data.component.html',
    styleUrls: ['./regulary-data.component.sass'],
    standalone: false
})

export class RegularyDataComponent implements OnInit, OnDestroy {

  topics: any;
  processedTopics: ProcessedTopic[] = [];
  loading = true;
  error = false;
  isLoader = false;
  selectedKeyword: string = '';
  parts: string[] = [];
  linkableKeywords: Map<string, string> = new Map([
    ['asos', '/registration'],
    ['new dialogys', 'https://newdialogys.renault.com/']
  ]);
  
  private destroy$ = new Subject<void>();
  private keywordRegex: RegExp;
  private translationCache = new Map<string, string>();

  constructor(
    private regulatoryDataService: RegulatoryDataService,
    private cdr: ChangeDetectorRef,
    private translate: TranslateService
  ) {
    const keywords = Array.from(this.linkableKeywords.keys()).join('|');
    this.keywordRegex = new RegExp(`(${keywords})`, 'gi');
  }

  ngOnInit() {
    if(!environment.production) {
      console.log('RegularyDataComponent: Initializing');
    }
    
    this.loadRegulatoryData();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadRegulatoryData(): void {
    this.loading = true;
    this.error = false;

    this.regulatoryDataService.getRegulatoryData()
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.loading = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: (data: any) => {
          if(!environment.production) {
            console.log('RegularyDataComponent: Data loaded, processing...');
          }
          this.topics = data;
          this.preprocessTopics();
          
          if(!environment.production) {
            console.log('RegularyDataComponent: Processing complete');
          }
        },
        error: (err: any) => {
          if(!environment.production) {
            console.error('RegularyDataComponent: Error', err);
          }
          this.error = true;
        }
      });
  }

  private preprocessTopics(): void {
    if(!this.topics) return;

    this.processedTopics = this.topics.map((topic: any) => ({
      title: this.getCachedTranslation(topic.title),
      details: topic.details.map((detail: any) => ({
        subject: this.getCachedTranslation(detail.subject),
        info: detail.info,
        processedTextSegments: this.processTextSegments(detail.info)
      }))
    }));
  }

  private getCachedTranslation(key: string): string {
    if(!this.translationCache.has(key)) {
      this.translationCache.set(key, this.translate.instant(key));
    }
    return this.translationCache.get(key)!;
  }

  private processTextSegments(info: string): Array<{ text: string; isLink: boolean; url?: string }> {
    const translatedInfo = this.getCachedTranslation(info);
    const parts = translatedInfo.split(this.keywordRegex);
    
    return parts.map(part => {
      const lowerPart = part.toLowerCase();
      const url = this.linkableKeywords.get(lowerPart);
      
      return {
        text: part,
        isLink: !!url,
        url: url
      };
    });
  }

  retryLoad(): void {
    this.translationCache.clear();
    this.loadRegulatoryData();
  }

  hasLinkableKeywords(info: string): boolean {
    const translatedInfo = this.translate.instant(info);
    const lowerInfo = translatedInfo.toLowerCase();
    
    for (const keyword of this.linkableKeywords.keys()) {
      if(lowerInfo.includes(keyword)) {
        const match = translatedInfo.match(new RegExp(keyword, 'i'));
        if(match) {
          this.selectedKeyword = match[0];
          this.segments(translatedInfo);
          return true;
        }
      }
    }
    
    this.selectedKeyword = '';
    return false;
  }

  segments(info: string) {
    const regex = new RegExp(`${this.selectedKeyword}`, 'gi');
    this.parts = info.split(regex);
  }

  processText(info: string): string[] {
    return this.translate.instant(info).split(this.keywordRegex);
  }
}
